import { useEffect, useState } from "react";
import { api, formatApiError, ROLES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { WhatsappLogo, PaperPlaneTilt, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Reusable WhatsApp nudge trigger — used on Expense Detail, Expenses list,
 * MyQueue, and Assets.
 *
 * Props:
 *   - kind: "expense" | "asset"
 *   - id: record id
 *   - number: display number (used only in fallback message)
 *   - variant: "button" (default, full-width row action) | "icon" (list rows) | "inline"
 *   - onSent: optional callback after successful nudge
 */
export default function WhatsAppNudge({
  kind, id, number, variant = "button", onSent, disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [sending, setSending] = useState(false);

  const path = kind === "asset" ? "/assets" : "/expenses";

  const loadTargets = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${path}/${id}/nudge-targets`);
      setData(r.data);
      setMessage(r.data.message_template || "");
      const first = (r.data.targets || [])[0];
      setSelectedId(first?.id || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load approvers");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sendNudge = async () => {
    if (!selectedId) return toast.error("Select an approver first.");
    setSending(true);
    try {
      const r = await api.post(`${path}/${id}/nudge`, {
        target_user_id: selectedId,
      });
      const waUrl = r.data.wa_url + encodeURIComponent(message || "");
      window.open(waUrl, "_blank", "noopener,noreferrer");
      toast.success("Opening WhatsApp — nudge logged in audit trail.");
      onSent?.();
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send nudge");
    } finally {
      setSending(false);
    }
  };

  const stop = (e) => { e.stopPropagation(); };

  const trigger = (() => {
    if (variant === "icon") {
      return (
        <button
          type="button"
          onClick={(e) => { stop(e); setOpen(true); }}
          disabled={disabled}
          title="Send WhatsApp reminder to pending approver"
          data-testid={`wa-nudge-icon-${kind}-${id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
        >
          <WhatsappLogo size={16} weight="bold" />
        </button>
      );
    }
    if (variant === "inline") {
      return (
        <button
          type="button"
          onClick={(e) => { stop(e); setOpen(true); }}
          disabled={disabled}
          data-testid={`wa-nudge-inline-${kind}-${id}`}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-emerald-700 hover:text-emerald-900 border border-emerald-300 hover:border-emerald-700 px-2.5 py-1 rounded-sm bg-white"
        >
          <WhatsappLogo size={12} weight="bold" /> Nudge
        </button>
      );
    }
    return (
      <Button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        data-testid={`wa-nudge-btn-${kind}-${id}`}
        className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-10"
      >
        <WhatsappLogo size={16} weight="bold" />
        WhatsApp Reminder
      </Button>
    );
  })();

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white rounded-sm max-w-lg w-[calc(100vw-2rem)] sm:w-full"
          data-testid="wa-nudge-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <WhatsappLogo size={20} weight="bold" className="text-emerald-600" />
              WhatsApp Reminder
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Opens WhatsApp with a pre-filled reminder for the pending
              approver{number ? ` on ${number}` : ""}. Every nudge is logged
              in the audit trail.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="p-6 text-sm text-zinc-500 text-center">Loading approvers…</div>
          )}

          {!loading && data && (
            <div className="space-y-4">
              {data.next_approver_role ? (
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                  Pending at
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 tracking-widest">
                    {ROLES[data.next_approver_role] || data.next_approver_role}
                  </span>
                </div>
              ) : (
                <div className="text-sm text-zinc-500">
                  {data.message || "No pending step — nothing to nudge."}
                </div>
              )}

              {(data.targets || []).length === 0 && data.next_approver_role && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 px-3 py-2">
                  No active {ROLES[data.next_approver_role] || data.next_approver_role} with a phone number
                  is assigned to this centre. Ask an admin to add a mobile
                  number under Administration → Users.
                </div>
              )}

              {(data.targets || []).length > 0 && (
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {data.targets.map((t) => (
                    <label
                      key={t.id}
                      data-testid={`wa-nudge-target-${t.id}`}
                      className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition ${
                        selectedId === t.id
                          ? "border-emerald-600 bg-emerald-50"
                          : "border-zinc-200 hover:border-zinc-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="wa-target"
                        value={t.id}
                        checked={selectedId === t.id}
                        onChange={() => setSelectedId(t.id)}
                        className="shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-900 truncate">{t.name}</div>
                        <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                          <Phone size={11} /> {t.phone}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {(data.targets || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">
                    Message
                  </div>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    className="rounded-sm border-zinc-300 text-sm"
                    data-testid="wa-nudge-message"
                  />
                </div>
              )}

              {(data.recent || []).length > 0 && (
                <div className="border-t border-zinc-200 pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    Recent nudges ({(data.recent || []).length})
                  </div>
                  <ul className="space-y-1 text-xs text-zinc-600 max-h-24 overflow-y-auto">
                    {data.recent.slice(0, 5).map((r) => (
                      <li key={r.id} className="flex justify-between gap-3">
                        <span className="truncate">
                          <b>{r.sender_name}</b> → {r.target_name}
                        </span>
                        <span className="text-zinc-400 tabular-nums shrink-0">
                          {new Date(r.at).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendNudge}
                  disabled={!selectedId || sending}
                  data-testid="wa-nudge-send"
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                >
                  <PaperPlaneTilt size={14} weight="bold" />
                  {sending ? "Opening…" : "Open WhatsApp"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
